/**
 * Undo/redo history for the tactics boards.
 *
 * Subscribes to the existing `tactics-layout-changed` custom event
 * (see `tactics3dStore.ts`) for a given `persistKey` and maintains an
 * in-memory stack of layout snapshots. The hook is mount-ready —
 * `undo()` and `redo()` write the previous/next entry back via
 * `saveTacticsLayout`, which the board's listener picks up and
 * rehydrates from.
 *
 * Self-suppression: writes performed BY undo/redo themselves emit a
 * layout-changed event. Without a guard those would push another
 * entry onto the stack and the undo/redo loop becomes self-feeding
 * (the canonical 756dc2c bug). We set `selfWriteRef = true` before
 * any internal save, and the change handler decrements/clears it
 * before bailing out.
 *
 * API:
 *   { canUndo, canRedo, undo, redo, push, clear }
 *
 * Stack shape: a single linear array of layout snapshots plus a
 * pointer (`indexRef`). `indexRef` points at the CURRENT layout —
 * undo moves it backward, redo forward. Pushing a new entry truncates
 * any redo tail (standard editor undo behaviour: branch on edit).
 *
 * Cap: 50 entries. Older entries are dropped from the front; the
 * pointer is shifted accordingly so it still references the same
 * logical state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TacticsLayout } from '@4cm/core/tactics';
import {
  loadTacticsLayout,
  onTacticsLayoutChanged,
  saveTacticsLayout,
} from './tactics3dStore';

const STACK_CAP = 50;

export interface UseTacticsHistory {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  /** Force-snapshot a layout. Mostly for testability. */
  push: (layout: TacticsLayout) => void;
  /** Drop all history. */
  clear: () => void;
}

/**
 * Strip the version + timestamp fields before re-saving so
 * `saveTacticsLayout` re-stamps them. This keeps `saved_at` honest
 * (the moment the entry was actually written back) and lets the
 * store reject stale schema versions on its own terms.
 */
function toSaveInput(layout: TacticsLayout): Omit<TacticsLayout, 'v' | 'saved_at'> {
  const { v: _v, saved_at: _saved_at, ...rest } = layout;
  void _v;
  void _saved_at;
  return rest;
}

export function useTacticsHistory(persistKey: string | undefined): UseTacticsHistory {
  // Stack of layout snapshots; `indexRef.current` points at the
  // currently-applied entry. -1 means "no history yet".
  const stackRef = useRef<TacticsLayout[]>([]);
  const indexRef = useRef<number>(-1);

  // When true, the next `tactics-layout-changed` event was triggered
  // by our own write (undo / redo). The handler consumes the flag and
  // skips pushing — preventing the self-feeding loop.
  const selfWriteRef = useRef<boolean>(false);

  // Re-render trigger. We can't use the stack itself in state (refs
  // avoid re-renders during the change handler) so we bump a tick
  // counter whenever canUndo / canRedo could have flipped.
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);

  const push = useCallback((layout: TacticsLayout) => {
    // Truncate any redo-tail before appending the new state.
    if (indexRef.current < stackRef.current.length - 1) {
      stackRef.current = stackRef.current.slice(0, indexRef.current + 1);
    }
    stackRef.current.push(layout);
    // Enforce cap from the front (oldest first); shift the pointer
    // to keep it referencing the same logical entry.
    while (stackRef.current.length > STACK_CAP) {
      stackRef.current.shift();
    }
    indexRef.current = stackRef.current.length - 1;
    bump();
  }, [bump]);

  // Listen for layout changes on this key and snapshot the new
  // layout. Self-triggered writes (undo/redo) skip the push via
  // `selfWriteRef`.
  useEffect(() => {
    if (!persistKey) return;
    // Seed with the current persisted layout if any — gives us
    // something to undo back TO once the first edit fires.
    const seed = loadTacticsLayout(persistKey);
    if (seed) {
      stackRef.current = [seed];
      indexRef.current = 0;
      bump();
    }
    return onTacticsLayoutChanged(persistKey, () => {
      if (selfWriteRef.current) {
        selfWriteRef.current = false;
        return;
      }
      const fresh = loadTacticsLayout(persistKey);
      if (!fresh) return;
      push(fresh);
    });
  }, [persistKey, push, bump]);

  const undo = useCallback(() => {
    if (!persistKey) return;
    if (indexRef.current <= 0) return;
    indexRef.current -= 1;
    const target = stackRef.current[indexRef.current];
    if (!target) return;
    selfWriteRef.current = true;
    saveTacticsLayout(persistKey, toSaveInput(target));
    bump();
  }, [persistKey, bump]);

  const redo = useCallback(() => {
    if (!persistKey) return;
    if (indexRef.current >= stackRef.current.length - 1) return;
    indexRef.current += 1;
    const target = stackRef.current[indexRef.current];
    if (!target) return;
    selfWriteRef.current = true;
    saveTacticsLayout(persistKey, toSaveInput(target));
    bump();
  }, [persistKey, bump]);

  const clear = useCallback(() => {
    stackRef.current = [];
    indexRef.current = -1;
    bump();
  }, [bump]);

  const canUndo = indexRef.current > 0;
  const canRedo = indexRef.current >= 0 && indexRef.current < stackRef.current.length - 1;

  return { canUndo, canRedo, undo, redo, push, clear };
}
